import React, { useMemo, useState } from 'react'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'

import {
    ChargePoint,
    FirmwareGeneralModel,
    useChargePointUpdateFirmware2Mutation,
} from '../../../../../common/redux/api/web-api'
import useText from '../../../../../common/hooks/useText'
import { Dialog } from '../../../../../common/uikit/Dialog'
import { Dropdown } from '../../../../../common/uikit/Dropdown'
import { useToast } from '../../../../../common/uikit/ToastProvider'

export type UpgradeFirmwareVersionDialogProps = {
    visible: boolean
    onHide: () => void
    chargePoint: ChargePoint
    availableFirmwares: FirmwareGeneralModel[] | null | undefined
    firmwareLoading: boolean
    getFirmwareQuerySuccess: boolean
}

export function UpgradeFirmwareVersionDialog({
    visible,
    onHide,
    chargePoint,
    firmwareLoading,
    getFirmwareQuerySuccess,
    availableFirmwares,
}: UpgradeFirmwareVersionDialogProps): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const [selectedFirmware, setSelectedFirmware] = useState<number>()

    const firmwareOptions = useMemo(
        () =>
            availableFirmwares?.flatMap((firmware) => {
                if (
                    firmware.id === undefined ||
                    firmware.firmwareVersion === undefined ||
                    firmware.firmwareVersion === null
                ) {
                    return []
                }

                return [{ value: firmware.id, label: String(firmware.firmwareVersion) }]
            }) ?? [],
        [availableFirmwares]
    )
    const [updateFirmware] = useChargePointUpdateFirmware2Mutation()

    const handleHide = () => {
        setSelectedFirmware(undefined)
        onHide()
    }

    const upgrade = async () => {
        if (!selectedFirmware) return

        try {
            await updateFirmware({
                chargePointId: chargePoint.id,
                updateChargerFirmwareModel: { firmwareId: selectedFirmware },
            }).unwrap()
            toast.notifySuccess(
                text('msgFWUpdateScheduleInfo', {
                    name: chargePoint.id,
                })
            )
        } catch {
            toast.notifyError(text('lblFirmwareVersionUpdatedFailed'))
        }

        handleHide()
    }

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={text('btnCancel')} text onClick={handleHide} type="button" />
            <Button
                label={text('btnUpdateFirmware')}
                onClick={() => void upgrade()}
                disabled={!selectedFirmware || firmwareLoading}
                type="button"
            />
        </div>
    )

    return (
        <Dialog
            header={text('lblUpgradeVersionModal')}
            visible={visible}
            onHide={handleHide}
            footer={footer}
            data-testid="upgrade-firmware-version-dialog"
        >
            {firmwareLoading && (
                <div className="flex align-items-center gap-2" data-testid="loading-message">
                    <ProgressSpinner className="w-2rem h-2rem" />
                    <span>{text('msgLoadingAvailableFirmwareVersions')}</span>
                </div>
            )}
            {getFirmwareQuerySuccess && (
                <Dropdown
                    id="firmware-options"
                    label={text('lblFirmwareVersion')}
                    options={firmwareOptions}
                    value={selectedFirmware ?? null}
                    onChange={(event) => {
                        setSelectedFirmware(event.value)
                    }}
                    showClear={false}
                    filter={false}
                />
            )}
        </Dialog>
    )
}
